/**
 * FO-328 (batch-38) — gedeelde upload-limieten voor client én server.
 *
 * Eén bron voor de maximale bestandsgrootte: de API-multipart-laag
 * (`apps/api/src/plugins/multipart.ts`), de route-validatie
 * (`apps/api/src/lib/uploads.ts` → `validateFileSize`) en de client-side
 * maat-check (portal `FotoOfBestandInput` / declaratie-form) lezen allemaal
 * deze constante. Drift tussen client-melding ("max 10 MB") en server-limiet
 * (25 MB) was een van de oorzaken van misleidende upload-fouten in de
 * onboarding-wizard.
 */
export const UPLOAD_MAX_BESTAND_BYTES = 25 * 1024 * 1024 // 25 MB
export const UPLOAD_MAX_BESTAND_MB = UPLOAD_MAX_BESTAND_BYTES / (1024 * 1024)

/**
 * FO-339 — gedeelde MIME-allowlist voor generieke document-uploads (bibliotheek,
 * werkbon-bijlagen, communicatie, declaraties, factuurbijlagen, ongevallen,
 * verzuim). Eén bron voor server (`apps/api/src/lib/uploads.ts` →
 * `validateMimeType`) én client (`accept`-attribuut + pre-check), zodat de
 * kopieën nooit meer uit elkaar lopen.
 *
 * Alleen OOXML voor Office-formaten: legacy `.doc`/`.xls`/`.ppt` worden bewust
 * geweigerd (de viewer-keten en downstream-verwerking rekenen op OOXML).
 * Smallere allowlists (onboarding-documenten, klantcontracten, foto-only-paden)
 * blijven bewuste subsets en spiegelen NIET deze lijst.
 */
export const UPLOAD_TOEGESTANE_MIME: ReadonlySet<string> = new Set([
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // docx
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // xlsx
  'application/vnd.openxmlformats-officedocument.presentationml.presentation', // pptx
])

/** Afgeleid `accept`-attribuut voor file-inputs (client-side picker-filter). */
export const UPLOAD_ACCEPT_MIME = [...UPLOAD_TOEGESTANE_MIME].join(',')

/** Leesbare opsomming voor UI-teksten en foutmeldingen. */
export const UPLOAD_TOEGESTAAN_LABEL = 'PDF, JPG, PNG, WebP, DOCX, XLSX, PPTX'

/** Bestandsextensie per toegestaan MIME-type (voedt de storage-key-suffix). */
export const UPLOAD_MIME_EXTENSIES: Readonly<Record<string, string>> = {
  'application/pdf': '.pdf',
  'image/jpeg': '.jpg',
  'image/png': '.png',
  'image/webp': '.webp',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
}

/**
 * MIME-types waaraan we een HEIC/HEIF-bestand herkennen (iPhone-standaard).
 * De server-allowlist accepteert deze bewust NIET (de viewer-keten kan geen
 * HEIC tonen); de client converteert waar mogelijk naar JPEG (Safari kan HEIC
 * decoderen) of toont een duidelijke omzet-tip.
 */
export const HEIC_MIME_TYPES: readonly string[] = [
  'image/heic',
  'image/heif',
  'image/heic-sequence',
  'image/heif-sequence',
]

/** Herken HEIC/HEIF op MIME-type óf bestandsextensie (sommige browsers geven een leeg type). */
export function isHeicBestand(input: { type?: string | null; name?: string | null }): boolean {
  const type = (input.type ?? '').toLowerCase()
  if (HEIC_MIME_TYPES.includes(type)) return true
  const name = (input.name ?? '').toLowerCase()
  return name.endsWith('.heic') || name.endsWith('.heif')
}

/**
 * Vaste gebruikersmelding voor een geweigerd HEIC-bestand. Gedeeld tussen de
 * client-side pre-check en de server-side 415 zodat de gebruiker op beide
 * paden dezelfde handelingsgerichte tekst ziet.
 */
export const HEIC_MELDING =
  'Dit is een HEIC-foto (iPhone-formaat) en die kunnen we niet verwerken. ' +
  'Tip: zet in de iPhone-instellingen Camera > Structuur op "Meest compatibel", ' +
  'of deel de foto eerst via Berichten/Mail (dan wordt hij automatisch JPEG).'
